import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AdminAuthContext = createContext(null);
const STORAGE_KEY = "gbs_admin_token";

export const AdminAuthProvider = ({ children }) => {
    const [token, setToken] = useState(null);
    const [checking, setChecking] = useState(true);

    // Bootstrap: load token from localStorage, verify with /admin/me (one-shot on mount)
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) { setChecking(false); return; }
        api.get("/admin/me", { headers: { Authorization: `Bearer ${stored}` } })
            .then(() => setToken(stored))
            .catch(() => localStorage.removeItem(STORAGE_KEY))
            .finally(() => setChecking(false));
    }, []);

    // Attach token to every axios request whenever it changes
    useEffect(() => {
        const id = api.interceptors.request.use((cfg) => {
            const t = token || localStorage.getItem(STORAGE_KEY);
            if (t) cfg.headers.Authorization = `Bearer ${t}`;
            return cfg;
        });
        return () => api.interceptors.request.eject(id);
    }, [token]);

    const login = useCallback(async (password) => {
        const { data } = await api.post("/admin/login", { password });
        localStorage.setItem(STORAGE_KEY, data.token);
        setToken(data.token);
        return data;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
    }, []);

    const value = useMemo(
        () => ({ token, isAuthed: !!token, checking, login, logout }),
        [token, checking, login, logout]
    );

    return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
    const ctx = useContext(AdminAuthContext);
    if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
    return ctx;
};
